import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Settings, LogOut, RotateCcw, Home, Menu } from 'lucide-react';
import { MaintenanceBanner } from './MaintenanceBanner';
import { LowCreditsBanner } from './LowCreditsBanner';
import { useSession } from '../contexts/SessionContext';

interface DashboardHeaderProps {
  showResetButton?: boolean;
  showDashboardButton?: boolean;
  onMenuClick?: () => void;
}

export function DashboardHeader({ showResetButton = true, showDashboardButton = false, onMenuClick }: DashboardHeaderProps) {
  const navigate = useNavigate();
  const { session } = useSession();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleSignOut = async () => {
    try {
      const { supabase } = await import('../lib/supabase');
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleResetClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmReset = () => {
    setShowConfirmDialog(false);
    navigate('/dashboard');
  };

  const handleCancelReset = () => {
    setShowConfirmDialog(false);
  };

  const getResetWarningContent = () => {
    if (!session) return null;

    const itemsToLose = [];
    if (session.article_content) itemsToLose.push('Je artikel');
    if (session.titles || session.selected_title) itemsToLose.push('Alle gegenereerde titels');
    if (session.intros || session.selected_intro) itemsToLose.push('Alle gegenereerde intro\'s');
    if (session.images || session.selected_image_url) itemsToLose.push('Alle gegenereerde afbeeldingen');

    return itemsToLose;
  };

  const itemsToLose = getResetWarningContent();

  return (
    <>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 lg:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {onMenuClick && (
                <button
                  onClick={onMenuClick}
                  className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              )}
              <img
                src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
                alt="Dibbi Logo"
                className="h-8 lg:hidden"
              />
              {showDashboardButton && (
                <button
                  onClick={() => navigate('/campaigns')}
                  className="flex items-center gap-1 text-sm text-gray-600 hover:text-[#FF6B35] transition-colors"
                >
                  <Home className="w-4 h-4" />
                  <span>Terug naar Campaigns</span>
                </button>
              )}
              {showResetButton && session && !showDashboardButton && (
                <button
                  onClick={handleResetClick}
                  className="flex items-center gap-1 text-sm text-gray-600 hover:text-[#FF6B35] transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span className="hidden sm:inline">Annuleer maken blog</span>
                  <span className="sm:hidden">Annuleer</span>
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/dashboard/settings"
                className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                title="Instellingen"
              >
                <Settings className="w-6 h-6" />
              </Link>
              <button
                onClick={handleSignOut}
                className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                title="Uitloggen"
              >
                <LogOut className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>
      <MaintenanceBanner />
      <LowCreditsBanner />

      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <h2 className="text-xl font-bold text-[#231F20]">Weet je het zeker?</h2>

            {itemsToLose && itemsToLose.length > 0 ? (
              <div className="space-y-3">
                <p className="text-gray-700">Dit wordt verwijderd:</p>
                <ul className="space-y-1">
                  {itemsToLose.map((item, index) => (
                    <li key={index} className="text-gray-700 flex items-start gap-2">
                      <span className="text-red-500">✗</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-gray-700 pt-2">
                  Je begint helemaal opnieuw met een nieuw idee.
                </p>
              </div>
            ) : (
              <p className="text-gray-700">
                Je begint opnieuw met een nieuw idee.
              </p>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleCancelReset}
                className="btn-secondary flex-1"
              >
                Doorgaan met blog
              </button>
              <button
                onClick={handleConfirmReset}
                className="btn-primary flex-1 bg-red-600 hover:bg-red-700"
              >
                Ja, annuleer blog
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
